import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, CheckCircle2, AlertTriangle, Copy } from "lucide-react";
import { requireAdmin } from "@/lib/admin-guard";
import {
  getPaypalConfig,
  createPaypalProduct,
  createPaypalPlan,
  activatePaypalPlan,
  getPaypalLiveDetails,
  listPaypalTestSubscriptions,
  cancelPaypalSubscriptionAdmin,
  refundPaypalSubscriptionAdmin,
  getPaypalSubscriptionAudit,
} from "@/lib/paypal-admin.functions";
import { toast } from "sonner";
import { TestSubscriptionsPanel } from "@/components/admin/PaypalTestSubscriptionsPanel";
import { PaypalPricingMigrationPanel } from "@/components/admin/PaypalPricingMigrationPanel";

export const Route = createFileRoute("/admin_/paypal")({
  ssr: false,
  beforeLoad: requireAdmin,
  head: () => ({
    meta: [
      { title: "PayPal Setup | Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminPaypalPage,
});

type Config = Awaited<ReturnType<typeof getPaypalConfig>>;

function CopyableId({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2">
      <div>
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
        <code className="text-sm">{value}</code>
      </div>
      <button
        onClick={() => {
          navigator.clipboard.writeText(value);
          toast.success("Copied");
        }}
        className="rounded-md border border-border p-1.5 hover:bg-muted"
        aria-label={`Copy ${label}`}
      >
        <Copy className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function AdminPaypalPage() {
  const fetchConfig = useServerFn(getPaypalConfig);
  const createProduct = useServerFn(createPaypalProduct);
  const createPlan = useServerFn(createPaypalPlan);
  const activate = useServerFn(activatePaypalPlan);
  const fetchLive = useServerFn(getPaypalLiveDetails);

  const [config, setConfig] = useState<Config | null>(null);
  const [live, setLive] = useState<Awaited<ReturnType<typeof getPaypalLiveDetails>> | null>(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<null | "product" | "plan" | "activate">(null);

  const reload = () => {
    setLoading(true);
    fetchConfig({ data: undefined as never })
      .then(setConfig)
      .catch((e) => toast.error("Failed to load", { description: String(e) }))
      .finally(() => setLoading(false));
  };
  const loadLive = () => {
    setLiveLoading(true);
    fetchLive({ data: undefined as never })
      .then(setLive)
      .catch((e) => toast.error("Failed to fetch live details", { description: String(e) }))
      .finally(() => setLiveLoading(false));
  };
  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function extractIssues(err: unknown): string[] | null {
    const msg = err instanceof Error ? err.message : String(err);
    const marker = "PayPal validation failed:";
    const idx = msg.indexOf(marker);
    if (idx === -1) return null;
    return msg
      .slice(idx + marker.length)
      .split(";")
      .map((s) => s.trim())
      .filter(Boolean);
  }

  function showError(title: string, err: unknown) {
    const issues = extractIssues(err);
    if (issues && issues.length) {
      toast.error(title, {
        description: (
          <ul className="list-disc ps-4 space-y-1">
            {issues.map((i, n) => (
              <li key={n}>{i}</li>
            ))}
          </ul>
        ),
      });
    } else {
      toast.error(title, { description: String(err) });
    }
  }

  async function onCreateProduct() {
    setBusy("product");
    try {
      const r = await createProduct({ data: undefined as never });
      toast.success(r.existed ? "Product already existed" : "Product created", {
        description: r.product.id,
      });
      reload();
    } catch (e) {
      showError("Create product failed", e);
    } finally {
      setBusy(null);
    }
  }

  async function onCreatePlan() {
    setBusy("plan");
    try {
      const r = await createPlan({ data: undefined as never });
      toast.success(r.existed ? "Plan already existed" : "Plan created (CREATED status)", {
        description: r.plan.id,
      });
      reload();
    } catch (e) {
      showError("Create plan failed", e);
    } finally {
      setBusy(null);
    }
  }


  async function onActivate() {
    if (
      !confirm(
        "Activate the PayPal plan? This makes it live and available for subscriptions. Public UI is still hidden.",
      )
    )
      return;
    setBusy("activate");
    try {
      const r = await activate({ data: undefined as never });
      toast.success(r.alreadyActive ? "Plan was already active" : "Plan activated", {
        description: r.plan.id,
      });
      reload();
    } catch (e) {
      toast.error("Activate failed", { description: String(e) });
    } finally {
      setBusy(null);
    }
  }

  const product = config?.product ?? null;
  const plan = config?.plan ?? null;
  const planActive = plan?.status === "ACTIVE";

  return (
    <div className="mx-auto max-w-2xl px-4 py-10">
      <h1 className="font-serif text-3xl">PayPal — live setup</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Bootstraps the live PayPal product and Founding Membership plan. No
        public UI exposes PayPal yet — this page is admin-only.
      </p>

      <div className="mt-6 rounded-xl border border-amber-300/60 bg-amber-50/60 p-4 text-sm text-amber-900">
        <div className="flex gap-2">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div className="space-y-2">
            <p className="font-medium">Billing configuration (current)</p>
            <ul className="list-disc ps-5 space-y-1">
              <li>Setup fee: <strong>none</strong>.</li>
              <li>Recurring: <strong>$14.99 / month, $59.99 / 6 months or $89.99 / year</strong> — infinite until cancelled.</li>
              <li>Subscription <code>start_time</code>: <strong>immediate</strong> — the member is charged at approval.</li>
              <li>First renewal charge: <strong>one month after approval</strong>, then monthly.</li>
              <li>Legacy members on the retired $14.99 plan keep their original price.</li>
            </ul>

          </div>
        </div>
      </div>

      {loading ? (
        <div className="mt-8 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading current state…
        </div>
      ) : (
        <div className="mt-8 space-y-6">
          <section className="rounded-2xl border border-border p-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="font-serif text-xl">1 · Product</h2>
                <p className="text-sm text-muted-foreground">
                  Recovery Trauma™ Membership (SERVICE / SOFTWARE)
                </p>
              </div>
              {product ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              ) : null}
            </div>
            {product ? (
              <div className="mt-4 space-y-2">
                <CopyableId label="Product ID" value={product.id} />
                <p className="text-xs text-muted-foreground">
                  Created {new Date(product.created_at).toLocaleString("en-GB")}
                </p>
              </div>
            ) : (
              <button
                onClick={onCreateProduct}
                disabled={busy !== null}
                className="mt-4 btn-gold inline-flex items-center gap-2 disabled:opacity-60"
              >
                {busy === "product" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                Create product
              </button>
            )}
          </section>

          <section className="rounded-2xl border border-border p-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="font-serif text-xl">2 · Plan</h2>
                <p className="text-sm text-muted-foreground">
                  Recovery Trauma™ Membership · $14.99 / month · $59.99 / 6 months · $89.99 / year — no setup fee
                </p>
              </div>
              {plan ? (
                <span
                  className={
                    "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase " +
                    (planActive
                      ? "bg-emerald-100 text-emerald-800"
                      : "bg-amber-100 text-amber-800")
                  }
                >
                  {plan.status}
                </span>
              ) : null}
            </div>
            {plan ? (
              <div className="mt-4 space-y-2">
                <CopyableId label="Plan ID" value={plan.id} />
                <p className="text-xs text-muted-foreground">
                  Created {new Date(plan.created_at).toLocaleString("en-GB")}
                  {plan.activated_at
                    ? ` · Activated ${new Date(plan.activated_at).toLocaleString("en-GB")}`
                    : ""}
                </p>
              </div>
            ) : (
              <button
                onClick={onCreatePlan}
                disabled={busy !== null || !product}
                className="mt-4 btn-gold inline-flex items-center gap-2 disabled:opacity-60"
              >
                {busy === "plan" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                Create plan (CREATED status)
              </button>
            )}
            {!product ? (
              <p className="mt-2 text-xs text-muted-foreground">
                Create the product first.
              </p>
            ) : null}
          </section>

          <section className="rounded-2xl border border-border p-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="font-serif text-xl">3 · Activate</h2>
                <p className="text-sm text-muted-foreground">
                  Flip the plan to <code>ACTIVE</code> so subscriptions can be
                  created. Public UI stays hidden until you wire it up.
                </p>
              </div>
            </div>
            <button
              onClick={onActivate}
              disabled={busy !== null || !plan || planActive}
              className="mt-4 inline-flex items-center gap-2 rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-60"
            >
              {busy === "activate" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              {planActive ? "Plan is already active" : "Activate plan"}
            </button>
          </section>

          <section className="rounded-2xl border border-border p-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="font-serif text-xl">4 · Live PayPal response (audit)</h2>
                <p className="text-sm text-muted-foreground">
                  Fetches the product and plan directly from PayPal so you can
                  verify the real billing_cycles, setup_fee and status.
                </p>
              </div>
              <button
                onClick={loadLive}
                disabled={liveLoading || (!product && !plan)}
                className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted disabled:opacity-60"
              >
                {liveLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                {live ? "Refresh" : "Fetch live details"}
              </button>
            </div>
            {live ? (
              <div className="mt-4 space-y-4">
                <div>
                  <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Product · {product?.id ?? "(none)"}
                  </div>
                  {live.productError ? (
                    <p className="text-xs text-red-700">Error: {live.productError}</p>
                  ) : (
                    <pre className="max-h-80 overflow-auto rounded-lg border border-border bg-muted/40 p-3 text-[11px] leading-snug">
{JSON.stringify(live.productLive, null, 2)}
                    </pre>
                  )}
                </div>
                <div>
                  <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Plan · {plan?.id ?? "(none)"} · status {plan?.status ?? "(none)"}
                  </div>
                  {live.planError ? (
                    <p className="text-xs text-red-700">Error: {live.planError}</p>
                  ) : (
                    <pre className="max-h-96 overflow-auto rounded-lg border border-border bg-muted/40 p-3 text-[11px] leading-snug">
{JSON.stringify(live.planLive, null, 2)}
                    </pre>
                  )}
                </div>
              </div>
            ) : (
              <p className="mt-3 text-xs text-muted-foreground">
                Click <em>Fetch live details</em> to pull the current product
                and plan objects from PayPal.
              </p>
            )}
          </section>
        </div>
      )}
      <div className="mt-8">
        <PaypalPricingMigrationPanel />

        <TestSubscriptionsPanel
          list={listPaypalTestSubscriptions}
          cancel={cancelPaypalSubscriptionAdmin}
          refund={refundPaypalSubscriptionAdmin}
          audit={getPaypalSubscriptionAudit}
        />
      </div>
    </div>
  );
}
